/*

    This file is part of Universal Sequences.
    Universal Sequences is free software: you can redistribute it and/or modif
    it under the terms of the GNU General Public License as published by
    the Free Software Foundation, either version 3 of the License, or
    (at your option) any later version.

    Universal Sequences is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
    GNU General Public License for more details.

    You should have received a copy of the GNU General Public License
    along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import Tone from 'tone';
import {MAP, DELETE, OFF} from 'constants/midi-map-modes.js';
import ResolutionAction from 'midi/ResolutionAction.js';
import ParameterLocks from 'effects/ParameterLocks.js';
import MixSession from 'session/MixSession.js';
import Notes from 'midi/Notes.js';
import MidiTrack from 'MidiTrack';
import InstrumentTrack from 'InstrumentTrack';
import {getPhotos, perTrack} from 'utils/getSamplesForMint.js';
import {channelMixerFactory} from './ChannelMixerFactory.js';
import MultiFreezeTrack from 'track/MultiFreezeTrack.js';
import {publishPatternSwitch} from 'messaging/messages.js';
import MultiSynthTrack from 'track/MultiSynthTrack.js';
import {deleteKey} from 'utils/objectMover.js';
import FMSynthTrack from 'track/FMSynthTrack.js';
import SampledInstrumentTrack from 'track/SampledInstrumentTrack.js';
import Z4Track from 'track/Z4Track.js';
import InstrumentRackTrack from 'track/InstrumentRackTrack.js';
import {BANG} from 'messaging/message-types.js';
import MetalSynthTrack from 'track/MetalSynthTrack.js';
import {ARRANGEMENT_VIEW} from 'constants/view-modes.js';
import AudioTrack from 'track/AudioTrack.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import SubSequence from './SubSequence.js';
import ResolutionChangeEvent from 'session/ResolutionChangeEvent.js';
import Mix from 'session/Mix.js';
import {RESOLUTION_CHANGE} from 'session/constants.js';
import {getNoteSequence} from 'tensorflow/Midifier.js';
import {setActionMidiMapSelection} from 'actions/index.js';
import {
    setCurrentScene,
    setGroupTracks,
    setFrozenTracks,
    setSubSequenceWarps,
    setShowPianoRoll,
    setEffectParameterValues,
    setTrackUpdates,
    setFrozenTracksPlaying,
    setEffectsChainsToShow,
    deleteTrackId,
    deleteAutomationsForEngine
} from 'actions/index.js';
import {setSequencePatternLocked} from 'actions/index.js';
import {setSequencePatternLocks} from 'actions/index.js';
import Groups from 'track/Groups.js';
import Midi from 'midi/Midi.js';
import Effects from 'effects/Effects.js';
import axios from 'axios';
import Track from 'track/Track.js';
import TrackGroup from 'track/TrackGroup.js';
import MutableTrack from 'track/MutableTrack.js';
import startaudiocontext from 'startaudiocontext';
import crop from 'engine/Crop.js';
import Pattern from 'pattern/Pattern.js';
import Sends from 'engine/Sends.js';
import Sampler from 'instruments/Sampler.js';
import AudioInputManager from 'engine/AudioInputManager.js';
import AudioSettings from 'engine/AudioSettings.js';
import ChannelMixer from 'engine/ChannelMixer.js';
import {setResolution} from 'actions/index.js';
import {setSequenceRolling} from 'actions/index.js';
import {setNumberOfPatterns} from 'actions/index.js';
import {setCurrentPattern} from 'actions/index.js';
import {setSelectedTrack} from 'actions/index.js';
import {newSequence} from 'actions/index.js';
import {updateStepsForAllTracks,
        setCurrentSubSequence,
       setSubSequences} from 'actions/index.js';
import OperationManager from 'operations/OperationManager.js';
import {SEQUENCE_ROLL} from 'session/constants.js';
import ethereum from 'decentralized/ethereum.js';
import EncryptedIpfs from 'decentralized/guilds/EncryptedIpfs.js';

const defaultDuration = 1;
const SAMPLE_RATE = 44300.0;
var engineId = 0;
window.Tone = Tone;

// Encapsulates the sound engine
class SequencerEngine {
    constructor(store, transport=Tone.Transport, aubio, stereoOnly, isOffline) {
        this.isOffline = isOffline;
        this.idToParameter = {};
        this.stereoOnly = stereoOnly;
        this.engineId = engineId++;
        this.transport.swingSubvision = '16n';
        this.ethereum = ethereum;
	this.context = Tone.Master.context;

        this.idToObject = {};
        this.nameToObjects = {};
        this.tickingObjects = [];

        // redux store
        this.store = store;

        this.encryptedIpfs = new EncryptedIpfs(this.store, ethereum.guilds, ethereum.encryption, ethereum.crypto);

        // the mix session keeps track of all the automations that happen
        // across a song 
        this.mix = new Mix(this);

        // channel mixer routes the outputs of audio to different outputs
        // if available (for example, SoundFlower 64 channel mode)
        this.channelMixer = channelMixerFactory.use(this);

        // audio input manager handle's chrome's available
        // input channels. 
        this.audioInputManager = new AudioInputManager(this.store);

        // the Sends of the project, i.e. the reverb send/the echo send
        this.sends = new Sends(false, this.channelMixer, this);

        // a sequence contains sub-sequences, each with their own
        // tracks and groups.
        this.subSequences = [];
        this.newSubSequence();

        this.audioSettings = new AudioSettings();

        this.recordingsCounter = 1;

        // operation manager keeps track of undo information, all operations
        // that mutate the sequence should go through this manager.
        // still have significant work ahead to ensure that all mutating operations
        // go through this
        this.operationManager = new OperationManager(this);

        // Parameter Locks are essentially groups of parameters that can be
        // changed simultaneously. See the mix page in the sequencer, for the sliders
        // that control this
        this.parameterLocks = [new ParameterLocks(), new ParameterLocks()];
        if (aubio !== undefined) {
            this.aubio = aubio;
        } else {
            this.initAubio();
        }

        window.sequencerEngine = this;

        this.resampleConfig = {
            samplesPerNote: 1,
        };

        //this.initAudioWorklets();
    }

    async initAudioWorklets() {
        if (this.audioWorkletsInitialized) {
            return;
        }
        await Tone.Master.context.audioWorklet.addModule(window.isNFT ? "clipper.js" : '/clipper.js');
        console.log("INITIALIZING WAVE FOLDER");
        this.audioWorkletsInitialized = true;
    }

    get transport() {
	return Tone.Transport;
    }

    initAubio() {
        /*
        Lame.load({
            vbrQuality: 3,
            debug: true
        }).then(lame => {
            console.log("loaded lame...", lame);
            this.lame = lame;
        });
        */

        window.Aubio().then((aubio) => {
            this.aubio = aubio;
        });

        // create genish
        window.genish.export( window )
        window.utilities.ctx = this.context._context;

        // helper for playing genish graphs
        window.play = function( v, output) { //, memType=Float32Array ) {
            let name = 'ugen' + ( Math.round( Math.random() * 100000 ) )
            /*if( dat !== undefined ) {
              dat.GUI.__all__.forEach( v => v.destroy() )
              dat.GUI.__all__.length = 0
              }*/
            var cb = window.utilities.playWorklet( v, name, undefined, undefined, false, false, output);
            
            return cb
        }
    }

    newSubSequence(id, track) {
        let subSequence = new SubSequence(this, track !== undefined);
        if (track) {
            subSequence.addTrack(track);
        }
        if (id !== undefined) {
            subSequence.setId(id);
        }
        this.addSubSequence(subSequence, track === undefined);

        if (this.scenes) {
            this.scenes.newSubSequence();
        }

        return subSequence;
    }

    deleteSubSequence(id=this.currentSubSequence.id) {
        if (this.subSequences.length === 1) {
            return;
        }
        let index = -1;
        for (let i in this.subSequences) {
            if (this.subSequences[i].id === id) {
                index = i;
                break;
            }
        }

        if (index < 0) {
            return;
        }

        this.scenes.deleteSubSequence(index);

        let filtered = this.subSequences.filter(
            x => x.id !== id)[0];
        this.switchToSubSequence(filtered.id);
        this.subSequences[index].dispose();
        this.subSequences.splice(index, 1);
        this.store.dispatch(setSubSequences(this.engineId, this.subSequences.map(x => x.id)));
    }

    addSubSequence(subSequence, updateCurrentSubSequence=true) {
        this.subSequences.push(subSequence);
        this.currentSubSequence = subSequence;

        this.store.dispatch(setSubSequences(
            this.engineId,
            this.subSequences.map(x => x.id)));
        this.store.dispatch(setCurrentPattern(subSequence.id, 0));
        if (updateCurrentSubSequence) {
            this.store.dispatch(setCurrentSubSequence(
                this.engineId, this.currentSubSequence.id));
        }
    }

    switchToSubSequence(id, updateTracks=true) {
        let subSequence = this.subSequences.filter(
            x => x.id === id)[0];

        if (subSequence !== undefined) {
            this.currentSubSequence = subSequence;
            this.store.dispatch(
                setCurrentSubSequence(
                    this.engineId,
                    this.currentSubSequence.id));
            this.store.dispatch(setNumberOfPatterns(this.currentSubSequence.id, this.numberOfPatterns()));
            if (updateTracks) {
                this.updateTracks();
                if (this.tracks[0]) {
                    this.switchToPattern(this.tracks[0].currentPattern);
                }
            }
            this.groups.updateGroups();
        }
    }

    switchToNextSubSequence() {
        let index = this.subSequences.indexOf(
            this.currentSubSequence);

        if (index < this.subSequences.length - 1) {
            index++;
        } else {
            index = 0;
        }
        this.switchToSubSequence(
            this.subSequences[index].id);
    }

    switchToPreviousSubSequence() {
        let index = this.subSequences.indexOf(
            this.currentSubSequence);

        if (index > 0) {
            index--;
        } else {
            index = this.subSequences.length - 1;
        }
        this.switchToSubSequence(
            this.subSequences[index].id);
    }

    get mixes() {
        return [... this.subSequences.map(s => s.mix),
                this.mix];
    }

    get tracks() {
        return this.currentSubSequence.tracks;
    }

    set tracks(x) {
        this.currentSubSequence.tracks = x;
    }

    get groups() {
        return this.currentSubSequence.groups;
    }

    set groups(x) {
        this.currentSubSequence.groups = x;
    }

    get sequenceId() {
        return this.getSequenceId();
    }

    getSequenceId() {
        return this.loadedSequenceId === undefined ?
            0 : this.loadedSequenceId;
    }

    setMasterVolume(vol, time) {
        this.groups.channelMixer.setVolume(vol, time);
    }

    getMasterVolume() {
        this.groups.channelMixer.getVolume();
    }

    set rollingResolution(resolution) {
        if (this.store.getState().mix.isRecordingMix) {
            let rollEvent = this.mix.getEvent(
                SEQUENCE_ROLL, this.automationStep);

            let resolutionChange = new ResolutionChangeEvent(resolution);
            resolutionChange.step = this.automationStep;
            rollEvent.addSubEvent(resolutionChange);
        } else if (this.state.mix.isPlayingMix) {
            // TODO: since we now use compound events to represent
            // a sequence roll + resolution change, we need to
            // somehow represent the hold
            this.mix.holdEvent(new ResolutionChangeEvent(resolution));
        }
    }


    set resolution(resolution) {
        if (this.resolution.isTriplet !== resolution.isTriplet) {
            for (let i in this.subSequences) {
                for (let j in this.subSequences[i].tracks) {
                    let t = this.subSequences[i].tracks[j];
                    if (t.pattern.needsSync) {
                        t.pattern.syncBuckets(this.resolution.isTriplet);
                    }
                }
            }
        }

        this.rollingResolution = resolution;
        if (this.state.midi.midiMapMode === MAP) {
            // we tell the midi mapping system that "resolution" was
            // changed so that the user can map a knob to it
            this.store.dispatch(setActionMidiMapSelection(
                new ResolutionAction(this, resolution)));
        }

                // dispatch to redux
        this.store.dispatch(setResolution(resolution));

        this.tracks.forEach(track => track.updatePatternRollStepNumber(resolution));
    }

    get rollingResolution() {
        if (this.store.getState().mix.isPlayingMix) {
            let rollEvent = this.mix.getEvent(
                SEQUENCE_ROLL, this.automationStep);
            if (rollEvent !== undefined) {
                let subEvent = rollEvent.getSubEvent(this.automationStep);
                return subEvent.getValue();
            }
        }

        return this.resolution;
    }

    get resolution() {
        return this.state.sequencer.resolution;
    }

    get displayResolution() {
        return this.state.sequencer.displayResolution;
    }

    get state() {
        return this.store.getState();
    }

    getSubSequenceName(id) {
        const NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];
        for (let i=0; i < this.subSequences.length; i++) {
            if (this.subSequences[i].id === id) {
                return NAMES[i];
            }
        }

        return '';
    }

    getSubSequence(id) {
        return this.subSequences.filter(
            s => s.id === id)[0];
    }

    // auditions a sample on a specific track, allowing the user
    // to later cancel the audition or say "yes thats a good sample, let's use it"
    auditionSound(sample, selectedTrack) {
        // auditions the sound on the selected track
        // first complete all auditions
        let track = this.tracks[selectedTrack];
        this.tracks
            .filter(t => t !== track)
            .filter(track => track.isAuditioning())
            .forEach(track => track.completeAudition());
        track.auditionSound(sample);
    }

    // adds a sample to a project
    // if selected track is not undefined, then we layer that sample onto the selected track
    // if selected slices are not undefined, then we add the sample with the selected slices set
    // up
    // if chord is not undefined, then we add a dream sampler (not actually used yet)
    async addSound(
        sample,
        selectedTrack,
        selectedSlices,
        chord,
        isRecording=false,
        samplesStream,
        addToPad=true,
        pad) {
        var samplerData = {
            slices: selectedSlices === undefined || selectedSlices.length == 0 ? undefined : selectedSlices,
            offset: 0,
            transpose: 0,
            volume: 0
        };

        if (selectedTrack === undefined) {
            // We are adding a new sound as a new track
            var track = new MutableTrack(
                this.tracks.length,
                this,
                this.mixSession,
                this.numberOfPatterns(),
                () => this.update(),
                this.currentSubSequence);
            
            // set up the sends and effects
            track.setSends(this.sends);
            track.setupEffects();

            // add the new track to the list of tracks for the project
            this.currentSubSequence.addTrack(track);

            // switch the new track to the current pattern of the track
            track.switchToPattern(this.currentPattern);
            await track.addSound(sample, samplerData, true);
            this.groups.addTrack(track);

            if (isRecording) {
                this.recordingsCounter++;
                this.newRecordingToSaveLater(sample);
            }

            if (samplesStream) {
                track.samplesStream = samplesStream;
            }

            if (!this.state.selector.sourceExploring) {
                this.selectTrack(this.tracks.length - 1);
            }

            // make every track disabled
            //this.tracks.forEach(track => track.setIsEnabled(false));

            // make the newly added track "enabled"
            //track.setIsEnabled(true);

            // make all tracks stop auditioning
            this.tracks
                .filter(track => track.isAuditioning())
                .forEach(track => track.completeAudition());

            track.updateSteps();
            if (addToPad) {
                this.currentSubSequence.addTrackToPadIfPossible(track);
            }
            return track;
        } else if (this.tracks.length > selectedTrack) {
            // we are adding a sound to an existing track, i.e. we're
            // layering the sounds together. 
            var track = this.tracks[selectedTrack]
            track.addSound(sample, samplerData);
            this.selectTrack(selectedTrack);
            return track;
        }
    }

    // transcribes a track's "main sample", as midi and adds a new midi track
    // to the project with the notes transcribed from the sample
    transcribeIntoMidi(track) {
        return new Promise((resolve, reject) => {
            let sampler = track.getSamplers()[0];
            let slice = sampler.getSlice(0);
            getNoteSequence(slice.buffer._buffer ).then(
                noteSequence => {
                    let notes = noteSequence.notes;
                    let track = this.addMultiSynthTrack();
                    track.addNotes(notes, sampler.tempo);
                    resolve();
                });

        });
    }

    // duplicates a given track to the project, copying all the parameters
    // of that track along with it
    async duplicateTrack(trackNumber) {
        if (this.tracks[trackNumber] === undefined) {
            return;
        }
        let pad = this.tracks[trackNumber].pad;
        let track = this.tracks[trackNumber].getCopy(undefined, this);
        this.setupNewTrack(track, true, true);
        track.setupEffects();
        if (pad) {
            let slotNumber = pad.slots.indexOf(false);
            if (slotNumber > -1) {
                pad.addTrack(track, slotNumber);
            } else {
                let pad = this.currentSubSequence.createPad();
                pad.addTrack(track, 0);
            }
        }
        this.currentSubSequence.addTrack(track);
        this.updateTracks();
        this.groups.addTrack(track);
        this.update();
    }

    deleteTracks(tracks) {
        this.tracks = this.tracks.filter(track => !tracks.includes(track));
        tracks.forEach(x => x.dispose());
    }

    // deletes a given track from the project
    deleteTrack(trackNumber, replaceWith) {
        if (this.tracks[trackNumber].getTrackType() == 'MIDI') {
            // first disconnect
            this.tracks[trackNumber].disconnect();
        }
        let trackToDelete = this.tracks[trackNumber];
        let sample = trackToDelete.sample;
        let indexOfSample = this.samplesToSync.indexOf(sample);
        if (indexOfSample > -1) {
            this.samplesToSync.splice(indexOfSample, 1);
        }

        for (let i in this.tracks) {
            if (this.tracks[i].chokes.choke === trackToDelete) {
                this.tracks[i].chokes.choke = undefined;
            }
        }

        // delete from group
        let group = trackToDelete.group;
        this.groups.deleteTrack(trackToDelete);
        this.groups.updateGroupNumbers();
        this.rollingManager.deleteTrack(trackToDelete);

        trackToDelete.subSequence.deleteTrackFromPads(trackToDelete);
        trackToDelete.subSequence.deleteTrackFromSequencers(trackToDelete);

        // delete track from tracks list
        if (replaceWith) {
            this.tracks[trackNumber] = replaceWith;
        } else {
            this.tracks.splice(trackNumber, 1);
        }

        this.tracks = [... this.tracks];

        if (this.cursor.track >= trackNumber) {
            this.cursor.track--;
            if (this.cursor.track < 0) {
                this.cursor.track = 0;
            }
        }

        this.scripting.deleteHistoryForTrack(trackNumber);
        if (this.keyboardEngine) {
            this.keyboardEngine.deleteTrack(trackNumber);
        }
        this.tracks.forEach(
            (track, i) => {
                track.trackNumber = i;
            });
        this.tracks.forEach(
            (track, i) => track.updateSteps());

        // delete whatever keys we need to delete from redux
        let subSequenceId = trackToDelete.subSequence.id;
        let effectsChainsToShow =
            this.state.effects.effectsChainToShow[subSequenceId] ||
            {};

        this.store.dispatch(setEffectsChainsToShow(
            subSequenceId,
            deleteKey(trackNumber, effectsChainsToShow)));

        this.dispatch(
            deleteTrackId(trackToDelete.id));
        this.updateSteps();
        this.update(); 

        this.selectTrack(Math.max(trackNumber - 1, 0));

        let warps = this.tracks.map(t => t.isWarped());
        this.store.dispatch(setSubSequenceWarps(subSequenceId, warps));

        // dispose the track
        trackToDelete.dispose();

        if (this.tracks.length === 0) {
            this.dispatch(setShowPianoRoll(false));
        }
    }

    deleteGroup(group) {
        this.tracks = this.tracks.filter(x => group.tracks.indexOf(x) === -1);
        this.groups.deleteGroup(group);
        this.store.dispatch(
            setGroupTracks(group.id, []));

        this.updateSteps();
        this.update(); 
    }

    // the max track's length
    get length() {
        let max = -1;
        for (let i in this.subSequences) {
            for (let j in this.subSequences[i].tracks) {
                let len = this.subSequences[i].tracks[j].length;
                if (len >= max) {
                    max = len;
                }
            }
        }

        return max;
    }

    // pattern related functions
    // adds new pattern to each track
    async newPattern(copy) {
        // get the pad effects volumes
        let fxVolumes = this.currentSubSequence.pads.map(
            x => x.fxMacro.macroParameter.get());

        let numberOfPatterns = this.numberOfPatterns() + 1;
        let a = new Date().getTime();
        this.currentSubSequence.newPattern();
        let promises = this.tracks.map((track) => track.newPattern(copy));
        await Promise.all(promises);
        let b = new Date().getTime();

        this.switchToPattern(this.tracks[0].patterns.length - 1);

        this.currentSubSequence.pads.forEach(
            (x, i) => x.fxMacro.macroParameter.set(fxVolumes[i]));
        this.store.dispatch(setNumberOfPatterns(
            this.currentSubSequence.id, numberOfPatterns));
        this.lfosTrack.newPattern(numberOfPatterns);
        this.envelopesTrack.newPattern(numberOfPatterns);
    }

    // deletes a pattern from the project
    deletePattern(i) {
        if (this.tracks.length == 0 ||
            this.tracks[0].patterns.length == 1) {
            return;
        }
        i = i || this.tracks[0].currentPattern;
        this.tracks.map((track) => track.deletePattern(i));
        var newPattern = i > 0 ? i - 1 : 0;
        this.switchToPattern(newPattern);
        this.store.dispatch(setNumberOfPatterns(this.currentSubSequence.id, this.numberOfPatterns()));

        this.lfosTrack.deletePattern(i);
        this.envelopesTrack.deletePattern(i);
    }

    get subSequenceMix() {
        return this.currentSubSequence.mix;
    }

    // switches the project's current pattern to be a given pattern number
    switchToPattern(i, isAutomation=false, subSequence=this.currentSubSequence, isScene=false, time, holdEvent=false) {
        if (i < 0 || subSequence.tracks.length <= 0 ||
            subSequence.tracks[0].patterns.length <= i) {
            return;
        }

        let currentPattern = this.currentPattern;
        if (this.currentPattern === i) {
            return;
        }
        publishPatternSwitch(subSequence, i);
        if (!isScene) {
            this.dispatch(setCurrentScene(undefined));
        }


        if (this.state.mix.isRecordingMix && !isAutomation) {
            subSequence.mix.addEvent(new PatternSwitchEvent(i));
        } else if (!isAutomation || holdEvent) {
            subSequence.mix.holdEvent(new PatternSwitchEvent(i));
        }
        
        let bulkUpdate = subSequence.lfosTrack.switchToPattern(currentPattern, i);
        bulkUpdate = {
            ... bulkUpdate,
            ... subSequence.envelopesTrack.switchToPattern(currentPattern, i)
        };
        this.switchToPatternHelper(i, subSequence, bulkUpdate, time);

        let patternLockedParameters =
            this.getAllPatternLockedParameters(currentPattern, i);
        patternLockedParameters
            .forEach(parameter => parameter.updateRedux());

        subSequence.findNodeByName("patterns")
            .forEach(
                x => x.effect.sendMessage("pattern switched", i+1));

        subSequence.pads.forEach(pad => pad.sendSliderLevels());

    }

    get currentPattern() {
        return this.getCurrentPattern(this.currentSubSequence);
            
    }

    getCurrentPattern(subSequence) {
        return this.store.getState()
            .sequencer.currentPattern[subSequence.id];
    }

    switchToPatternHelper(p, subSequence=this.currentSubSequence, bulkUpdate, time) {
        let bulkUpdates = subSequence.tracks.map((track) => track.switchToPattern(p, undefined, time));
        if (!window.explore) { 
            let trackUpdates = {};
            for (let i in bulkUpdates) {
                let trackUpdate = bulkUpdates[i];
                if (trackUpdate.volume) {
                    trackUpdates[trackUpdate.volume.id] = {
                        volume: trackUpdate.volume.volume,
                        panning: trackUpdate.panning.panning,
                        mute: trackUpdate.mute.mute
                    };
                }
                bulkUpdate = {
                    ... bulkUpdate,
                    ... bulkUpdates[i].bulkUpdate
                };
            }
            this.store.dispatch(setEffectParameterValues(bulkUpdate));
            this.store.dispatch(setTrackUpdates(trackUpdates));
        }
        this.store.dispatch(setCurrentPattern(subSequence.id, p));

        if (this.viewMode !== ARRANGEMENT_VIEW) {
            this.updateSteps();
        }
    }

    updateSteps(updatePattern) {
        let action = this.tracks.map(track => track.getUpdateStepsAction());
        this.store.dispatch(updateStepsForAllTracks(action));
    }

    // switches to the last pattern of the project (used when user hits
    switchToLastPattern() {
        if (this.tracks.length == 0) {
            return;
        }

        this.switchToPattern(this.numberOfPatterns() - 1);
    }

    // switches the project's current pattern to be the pattern
    // right before the current pattern
    switchToPreviousPattern() {
        if (this.tracks.length == 0) {
            return;
        }
        let currentPattern = this.tracks[0].currentPattern;
        let patternsLength = this.tracks[0].patterns.length;

        let newPattern = currentPattern - 1 % patternsLength;
        this.switchToPattern(newPattern);
    }

    // switches the project's current pattern to be the pattern
    // after from the current pattern
    switchToNextPattern() {
        if (this.tracks.length == 0) {
            return;
        }
        let currentPattern = this.tracks[0].currentPattern;
        let patternsLength = this.tracks[0].patterns.length;
        let newPattern = currentPattern + 1 % patternsLength;
        this.switchToPattern(newPattern);
    }

    // moves a pattern's position 
    movePatternTo(patternFrom, patternTo) {
        this.tracks.forEach(track => track.movePatternTo(patternFrom, patternTo));
        this.switchToPattern(patternTo);

        this.lfosTrack.movePatternTo(patternFrom, patternTo);
    }

    numberOfPatterns() {
        return this.tracks.length == 0 ? 1  : this.tracks[0].patterns.length;
    }

    // returns if all tracks are loaded
    isLoaded(i) {
        return this.tracks !== undefined &&
            this.tracks[i] !== undefined &&
            this.tracks[i].isLoaded();
    }

    // used for upating the callback for updating the frontend.
    // TODO: remove this, since we use Redux like civilized people
    setUpdateCallback(updateCallback) {
        this.callback = updateCallback;
    }

    // Updates the frontend of whatever caller of this class
    update(sequenceId) {
        if (!window.isNFT && !window.projectId && sequenceId && !window.multiMix && !window.documentation && !window.landing &&
           !this.tokenId) {
            //if (!window.isNFT) {
            window.history.pushState('page2', 'Title', '/v3/' + sequenceId);
            // }
        }
    }

    addTrack(track) {
        this.currentSubSequence.addTrack(track);
        track.setupEffects();
        this.groups.addTrack(track);
        this.setupNewTrack(track);
    }

    // add a new audio track to the project
    addAudioTrack() {
         var track = new AudioTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
             () => this.update(),
             this.currentSubSequence);
        this.setupNewTrack(track);
        track.warp();
        return track;
    }

    addMultiFreezeTrack(skipGroup=false, skipAdd=false) {
        var track = new MultiFreezeTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track, skipAdd);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addZ4Track(skipGroup=false, skipAdd=false) {
        var track = new Z4Track(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track, skipAdd);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addSampledInstrumentTrack(skipGroup=false, skipAdd=false) {
        var track = new SampledInstrumentTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track, skipAdd);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addInstrumentRackTrack(skipGroup=false, skipAdd=false) {
        var track = new InstrumentRackTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track, skipAdd);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addInstrumentTrack(skipGroup=false) {
        var track = new InstrumentTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addFMSynthTrack(skipGroup=false) {
        var track = new FMSynthTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track);
        return track;
    }

    // add a new instrument track (i.e. synth) to the project
    addMetalSynthTrack(skipGroup=false) {
        var track = new MetalSynthTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        this.groups.addTrack(track);
        this.setupNewTrack(track);
        return track;
    }


    // add a new instrument track (i.e. synth) to the project
    addMultiSynthTrack(skipGroup=false) {
        var track = new MultiSynthTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence,
            true);
        this.groups.addTrack(track);
        this.setupNewTrack(track);
        track.preset.name = "Empty Patch";
        track.presetName = "Empty Patch";
        return track;
    }

    // add a new midi track (i.e. external instrument) to the project
    addMidiTrack() {
        var track = new MidiTrack(
            this.tracks.length,
            this,
            this.mixSession,
            this.numberOfPatterns(),
            () => this.update(),
            this.currentSubSequence);
        track.setMidiEngine(this.midi);
        track.setAudioSettings(this.audioSettings);
        this.setupNewTrack(track);
        return track;
    }

    // helper method when adding a new track to the project
    setupNewTrack(track, skipAdd=false, skipSends=false, updateTracks=true, isOffline=false) {
        if (track.isRecordable()) {
            this.setupRecordableTrack(track, skipAdd);
        }
        track.sequencerEngine = this;
        if (!skipSends) {
            track.setSends(this.sends);
        }
        track.setKeyboardEngine(this.keyboardEngine);
        if (!skipAdd) {
            this.currentSubSequence.addTrack(track);
        } 
        if (skipAdd) {
            track.switchToPattern(0);
        } else {
            track.switchToPattern(this.tracks[0].currentPattern);
            if (!isOffline) {
                this.currentSubSequence.addTrackToPadIfPossible(track);
            }
        }

        if (updateTracks) {
            this.update();
            this.updateTracks();
        }
    }

    setupRecordableTrack(track, skipGroup=false) {
        track.setAudioInputManager(this.audioInputManager);
        if (!skipGroup) {
            this.groups.addTrack(track);
        }
        track.setSequencerEngine(this);
    }

    // updates the redux store for all the track's internal state
    updateTracks(syncBuckets=false) {
        this.tracks.forEach((track, i) => {
            track.trackNumber = i;
            track.sequencerEngine = this;
            track.patterns.forEach(pattern => pattern.sequencerEngine = this);
        });

        if (!window.explore) {
            let updateData = this.tracks.map(track => track.getUpdateStepsAction(syncBuckets));
            this.store.dispatch(updateStepsForAllTracks(updateData));
        }
    }

    // gets rid of everything in the track, freeing the memory
    // effectively, this SequencerEngine object is "dead" now
    dispose() {
        if (this.playSequence) {
            this.playSequence.dispose();
        }
        for (let id in this.loadedSamples) {
            this.loadedSamples[id].dispose();
        }
        if (this.metronome) {
            this.metronome.dispose();
        }
        this.metronome = undefined;
        this.loadedSamples = {};
        channelMixerFactory.release(this.channelMixer);
        this.channelMixer = undefined;
        this.sends.dispose();
        this.subSequences.forEach(s => s.disposeParameters());
        this.subSequences.forEach(s => s.dispose());
        this.store.dispatch(deleteAutomationsForEngine(this.sequenceId));
        this.audioInputManager.dispose();
        this.idToParameter = {};
    }

    newSequence() {
        for (let i in this.subSequences) {
            let sub = this.subSequences[i];
            sub.dispose();
        }
        this.subSequences = [];
        this.newSubSequence();
        this.mix = new Mix(this);
        this.update();
        this.store.dispatch(setNumberOfPatterns(this.currentSubSequence.id, 1));
        this.store.dispatch(newSequence());
        this.loadedSequenceId = undefined;
        this.stop();
    }

    scrollToSelectedTrack() {
    }

    set patternLocks(patternLocks) {
        this.store.dispatch(
            setSequencePatternLocks(patternLocks));
    }

    togglePatternLock() {
        this.store.dispatch(
            setSequencePatternLocked(
                 this.currentPattern, !this.patternLocked));
    }

    get viewMode() {
        return this.state.sequencer.viewMode;
    }

    get patternLocked() {
        let patterns = this.store.getState()
            .sequencer.patternLocks;
        return patterns[this.currentPattern] || false;
    }

    getAllPatternLockedParameters() {
        let parameters = [... this.parameters, ... this.currentSubSequence.getAllParameters()];
        return parameters
            .filter(p => p.hasPatternLockedValues());
    }

    get guildId() {
        return this.state.guilds.selectedGuild ?
            this.state.guilds.selectedGuild.guildId : 0;
    }

    get metronomeOn() {
        return this.metronome && !this.metronome.muted;
    }

    set metronomeOn(on) {
        this.metronome.mute(!on);
    }

    getScale() {
        let notes = new Set();
        for (let i in this.tracks) {
            let track = this.tracks[i];
            for (let j in track.patterns) {
                track.patterns[j].getEasyPattern().getAllSteps()
                    .map(s => notes.add(Notes.fromTranspose(s.transpose).note));
            }
        }
        return Array.from(notes);
    }

    dispatch(f) {
        this.store.dispatch(f);
    }

    createPad(fromTracks) {
        this.currentSubSequence.createPad(fromTracks);
    }

    getSamplesPerTrack() {
        return perTrack(this);
    }

    compile() {
        for (let subSequence of this.subSequences) {
            let _nodes = subSequence.findNodeByName("three.canvas");
            _nodes.forEach(
                x => x.effect.sendMessage("size", [x.effect.width, x.effect.height]));
            let nodes = subSequence.findNodeByName("gl.material");
            for (let node of nodes) {
                node.refresh();
            }
        }
    }

    getPhotos() {
        return getPhotos(this);
    }
}   

export default SequencerEngine;
